import { createSelector } from '@reduxjs/toolkit';

import { EventUserSchema, UserRole } from 'types';
import {
  convertInviteeToEventUser,
  EMPTY_EVENT,
  EMPTY_EVENT_SLICE,
  EMPTY_EVENT_STATE,
  EMPTY_EVENT_USER,
  sortEventUsersByDate
} from 'utils';
import { RootState } from 'store/reducers';
import { EventSlice, EventSliceMeta, EventSliceSession, EventSliceState, EventsSlice } from 'store/slices/events';

export const eventsSelector = (state: RootState): EventsSlice => state.events;

export const eventSliceSelector = (eventCode: string) =>
  createSelector<RootState, EventsSlice, EventSlice>(
    state => eventsSelector(state),
    events => events[eventCode] || EMPTY_EVENT_SLICE
  );

export const eventSessionSelector = (eventCode: string) =>
  createSelector<RootState, EventSlice, EventSliceSession>(
    eventSliceSelector(eventCode),
    eventSlice => eventSlice.session
  );

export const eventSessionTokenSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceSession, string>(
    eventSessionSelector(eventCode),
    eventSession => eventSession.token
  );

export const metaSelector = (eventCode: string) =>
  createSelector<RootState, EventSlice, EventSliceMeta>(
    state => eventSliceSelector(eventCode)(state),
    eventSlice => eventSlice.meta || EMPTY_EVENT
  );

export const stateSelector = (eventCode: string) =>
  createSelector<RootState, EventSlice, EventSliceState>(
    state => eventSliceSelector(eventCode)(state),
    eventSlice => eventSlice.state || EMPTY_EVENT_STATE
  );

export const eventNameSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceMeta, string>(
    state => metaSelector(eventCode)(state),
    eventMeta => eventMeta?.name || ''
  );

export const allUsersSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema[], EventUserSchema[], EventUserSchema[]>(
    state => stageUsersSelector(eventCode)(state),
    state => inviteesSelector(eventCode)(state),
    state => viewersSelector(eventCode)(state),
    (stageUsers, invitees, viewers) => [
      ...stageUsers,
      ...invitees.map(invitee => convertInviteeToEventUser(invitee)),
      ...viewers
    ]
  );

export const userSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema>(
    state => allUsersSelector(eventCode)(state),
    eventUsers => {
      const eventUser = eventUsers.find(user => user.username === username);

      return eventUser || EMPTY_EVENT_USER;
    }
  );

export const userRoleSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema, UserRole>(
    state => userSelector(eventCode, username)(state),
    user => user.role
  );

export const stageUsersSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema[], EventUserSchema[], EventUserSchema[]>(
    state => hostsSelector(eventCode)(state),
    state => moderatorsSelector(eventCode)(state),
    state => speakersSelector(eventCode)(state),
    (hosts, moderators, speakers) => sortEventUsersByDate([...hosts, ...moderators, ...speakers])
  );

export const stageUsersCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => stageUsersSelector(eventCode)(state),
    stageUsers => stageUsers.length || 0
  );

export const hostsSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.onStageUsers.hosts)
  );

export const speakersSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.onStageUsers.speakers)
  );

export const moderatorsSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.onStageUsers.moderators)
  );

export const allInviteesSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.invitees)
  );

export const joinedInviteesSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema[]>(
    state => allInviteesSelector(eventCode)(state),
    allInvitees => allInvitees.filter(invitee => !!invitee.joinedOnStageDate)
  );

export const notJoinedInviteesSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema[]>(
    state => allInviteesSelector(eventCode)(state),
    allInvitees => allInvitees.filter(invitee => !invitee.joinedOnStageDate)
  );

export const inviteesSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema[]>(
    state => notJoinedInviteesSelector(eventCode)(state),
    invitees => invitees
  );

export const inviteesCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => inviteesSelector(eventCode)(state),
    invitees => invitees.length || 0
  );

export const viewersSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.viewers)
  );

export const viewerCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => viewersSelector(eventCode)(state),
    viewers => viewers.length || 0
  );

export const memberCountSelector = (eventCode: string) =>
  createSelector<RootState, number, number, number>(
    state => stageUsersCountSelector(eventCode)(state),
    state => viewerCountSelector(eventCode)(state),
    (stageUsersCount, viewerCount) => stageUsersCount + viewerCount
  );

export const requestsSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.joinRequests)
  );

export const requestsCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => requestsSelector(eventCode)(state),
    requests => requests?.length || 0
  );

export const hiddenRequestsSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.hiddenJoinRequests)
  );

export const hiddenRequestsCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => hiddenRequestsSelector(eventCode)(state),
    hiddenRequests => hiddenRequests?.length || 0
  );

export const pendingUsersSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, EventUserSchema[]>(
    state => stateSelector(eventCode)(state),
    eventState => sortEventUsersByDate(eventState.pendingUsers)
  );

export const pendingUsersCountSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], number>(
    state => pendingUsersSelector(eventCode)(state),
    requests => requests?.length || 0
  );

export const filledSeatsCountSelector = (eventCode: string) =>
  createSelector<RootState, number, number, number, number>(
    state => stageUsersCountSelector(eventCode)(state),
    state => inviteesCountSelector(eventCode)(state),
    state => pendingUsersCountSelector(eventCode)(state),
    (stageUsersCount, inviteesCount, pendingUsersCount) => stageUsersCount + inviteesCount + pendingUsersCount
  );

export const isHandRaiseEnabledSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, boolean>(
    state => stateSelector(eventCode)(state),
    eventState => eventState.handRaiseEnabled
  );

export const isHandRaisedSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema, EventUserSchema[], EventUserSchema[], boolean>(
    state => userSelector(eventCode, username)(state),
    state => requestsSelector(eventCode)(state),
    state => hiddenRequestsSelector(eventCode)(state),
    (localUser, requests, hiddenRequests) =>
      localUser.role === UserRole.VIEWER &&
      (!!requests.find(request => request.username === localUser.username) ||
        !!hiddenRequests.find(request => request.username === localUser.username))
  );

export const isInvitedToStageSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => pendingUsersSelector(eventCode)(state),
    pendingUsers => (username ? !!pendingUsers.find(pendingUser => pendingUser.username === username) : false)
  );

export const isInviteeSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => allInviteesSelector(eventCode)(state),
    invitees => !!invitees.find(invitee => invitee.inviteeCode === username)
  );

export const isHostSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => hostsSelector(eventCode)(state),
    hosts => !!hosts.find(host => host.username === username)
  );

export const isSpeakerSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => speakersSelector(eventCode)(state),
    speakers => !!speakers.find(speaker => speaker.username === username)
  );

export const isModeratorSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => moderatorsSelector(eventCode)(state),
    moderators => !!moderators.find(moderator => moderator.username === username)
  );

export const isStageUserSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, boolean, boolean, boolean, boolean>(
    state => isHostSelector(eventCode, username)(state),
    state => isSpeakerSelector(eventCode, username)(state),
    state => isModeratorSelector(eventCode, username)(state),
    (isHost, isSpeaker, isModerator) => isHost || isSpeaker || isModerator
  );

export const isViewerSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, EventUserSchema[], boolean>(
    state => viewersSelector(eventCode)(state),
    viewers => !!viewers.find(viewer => viewer.username === username)
  );

export const isAnonymousViewerSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, boolean, boolean, boolean, boolean>(
    state => isStageUserSelector(eventCode, username)(state),
    state => isInviteeSelector(eventCode, username)(state),
    state => isViewerSelector(eventCode, username)(state),
    (isStageUser, isInvitee, isViewer) => !isStageUser && !isInvitee && !isViewer
  );

export const isPassiveViewerSelector = (eventCode: string, username?: string | null) =>
  createSelector<RootState, boolean, boolean, boolean, boolean, boolean, boolean>(
    state => isStageUserSelector(eventCode, username)(state),
    state => isInviteeSelector(eventCode, username)(state),
    state => isInvitedToStageSelector(eventCode, username)(state),
    state => isViewerSelector(eventCode, username)(state),
    state => isAnonymousViewerSelector(eventCode, username)(state),
    (isStageUser, isInvitee, isInvitedToStage, isViewer, isAnonymousViewer) =>
      !isStageUser && !isInvitee && !isInvitedToStage && (isViewer || isAnonymousViewer)
  );

export const authorSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema[], EventUserSchema>(
    state => hostsSelector(eventCode)(state),
    hosts => hosts[0]
  );

export const authorNameSelector = (eventCode: string) =>
  createSelector<RootState, EventUserSchema, string>(
    state => authorSelector(eventCode)(state),
    author => author?.name
  );

export const isRecordedSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, boolean>(
    state => stateSelector(eventCode)(state),
    eventState => !!eventState.recordingEnabled
  );

export const startDateSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, Date>(
    state => stateSelector(eventCode)(state),
    eventState => new Date(eventState.startDate)
  );

export const endDateSelector = (eventCode: string) =>
  createSelector<RootState, EventSliceState, Date>(
    state => stateSelector(eventCode)(state),
    eventState => new Date(eventState.endDate)
  );
